import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

interface VideoShowcaseProps {
  projectTitle: string;
  coverImage: string;
}

export default function VideoShowcase({ projectTitle, coverImage }: VideoShowcaseProps) {
  const { ref, isVisible } = useScrollReveal();
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <section ref={ref} className="w-full py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-16">
        <div className={`text-center mb-10 md:mb-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-[11px] font-bold text-[#d4853a] uppercase tracking-[0.2em] mb-3">
            Project Showcase
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-[2.25rem] font-extrabold text-charcoal-300 leading-tight mb-4">
            Experience the <span className="text-[#d4853a]">Vision</span>
          </h2>
          <p className="text-[15px] text-charcoal-50 max-w-xl mx-auto leading-relaxed">
            Watch the project overview and see why this is Islamabad&apos;s most promising investment opportunity.
          </p>
        </div>

        {/* Cinematic Video Container */}
        <div
          className={`relative w-full rounded-2xl md:rounded-3xl overflow-hidden shadow-[0_20px_80px_rgba(0,0,0,0.15)] transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="relative aspect-[21/9] md:aspect-[21/8]">
            {!isPlaying ? (
              <>
                {/* Cover Image */}
                <img
                  src={coverImage}
                  alt={`${projectTitle} video preview`}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />

                {/* Dark overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/80 via-[#0a0a0a]/30 to-[#0a0a0a]/50" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/40 to-transparent" />

                {/* Glassmorphism panel */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-4">
                  <div>
                    <div className="text-[11px] font-bold text-[#d4853a] uppercase tracking-[0.2em] mb-2">
                      Watch Project Overview
                    </div>
                    <h3 className="text-lg md:text-xl lg:text-2xl font-extrabold text-white max-w-lg leading-tight">
                      {projectTitle}
                    </h3>
                  </div>
                </div>

                {/* Play Button */}
                <button
                  onClick={() => setIsPlaying(true)}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 group"
                  aria-label="Play project overview video"
                >
                  <div className="relative">
                    {/* Outer glow ring */}
                    <div className="absolute inset-0 rounded-full bg-[#d4853a]/20 animate-ping" style={{ animationDuration: '2s' }} />
                    {/* Main button */}
                    <div className="relative w-20 h-20 md:w-24 md:h-24 flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm shadow-[0_12px_40px_rgba(0,0,0,0.3)] transition-all duration-500 group-hover:scale-110 group-hover:bg-white group-hover:shadow-[0_16px_50px_rgba(0,0,0,0.4)]">
                      <i className="ri-play-fill text-3xl md:text-4xl text-[#d4853a] ml-1" />
                    </div>
                  </div>
                </button>
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0a]">
                <div className="text-center">
                  <i className="ri-movie-line text-4xl text-[#d4853a] mb-3" />
                  <p className="text-white/60 text-sm">Video playback would load here</p>
                  <p className="text-white/40 text-xs mt-1">Project overview video for {projectTitle}</p>
                  <button
                    onClick={() => setIsPlaying(false)}
                    className="mt-4 px-5 py-2 bg-white/10 text-white text-sm rounded-lg hover:bg-white/20 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}